import styled, { css } from 'styled-components';
import {
    ANIMATION_CURVE,
    ANIMATION_DURATION,
    getSpacingToken,
    getTextTokenPreset,
    getToken,
    MEDIA_QUERY,
    Size,
    Stack,
    StackItem,
    StyledTransitionGroup,
    StyledTransitionGroupProps,
    TransitionGroupChildBaseProps,
    ZIndex,
} from '@optimus-web/core';

export const getSidepanelWidth = ({ width }: { width: Size }) => {
    switch (width) {
        case 'small':
            return 468;
        case 'large':
            return 928;
        default:
            return 698;
    }
};

const PREVENT_SHRINK = css`
    min-width: 0;
    flex-shrink: 0;
`;

interface SidePanelContainer {
    opened: boolean;
    width: Size;
}

export const SidePanelContainer = styled.div<SidePanelContainer>`
    background-color: ${getToken('backgroundStaticFlat')};
    border-left: ${getToken('borderWidth150')}px solid ${getToken('borderStaticPrimary')};
    position: absolute;
    bottom: 0;
    right: 0;
    transition: transform ${ANIMATION_DURATION.default}ms ${ANIMATION_CURVE.default};
    ${(props) => `transform: translateX(${props.opened ? '0' : '100%'});`}
    height: 100%;
    width: 100%;
    max-width: ${getSidepanelWidth}px;
    pointer-events: initial;
    display: flex;
    flex-direction: column;

    ${({ width }) =>
        width &&
        width !== 'small' &&
        css`
            ${MEDIA_QUERY.tabletMax} {
                max-width: calc(100% - ${getSpacingToken('900')}px);
            }
        `}
`;

export const SidePanelHeader = styled.div<{ hasScrollBar?: boolean }>`
    display: flex;
    align-items: flex-start;
    gap: ${getSpacingToken('200')}px;
    padding: ${getSpacingToken('300')}px;
    border-bottom: ${getToken('borderWidth100')}px solid transparent;
    transition: ${ANIMATION_DURATION.fast}ms border ease-out;

    ${({ hasScrollBar }) =>
        hasScrollBar &&
        css`
            border-color: ${getToken('borderStaticSecondary')};
            background-color: ${getToken('backgroundStaticRaised')};
        `};
`;

export const SidePanelHeaderCloseContainer = styled(StackItem)`
    ${PREVENT_SHRINK};
`;

export const SidePanelHeaderTitleWrapper = styled(Stack)`
    flex-grow: 1;
    min-width: 0;
`;

export const SidePanelHeaderTitle = styled(Stack)`
    color: ${getToken('textStaticPrimary')};
    ${getTextTokenPreset('titleMediumStrong')};

    & > * {
        min-width: 0;
    }
`;

export const SidePanelHeaderSubtitle = styled(Stack)`
    color: ${getToken('textStaticTertiary')};
    ${getTextTokenPreset('bodySmall')};
`;

export const TitleTagContainer = styled(Stack)`
    flex-shrink: 0;
    margin-top: ${getSpacingToken('25')}px;
`;

export const SidePanelHeaderAction = styled(Stack)`
    ${PREVENT_SHRINK};
`;

export const SidePanelContent = styled.div`
    padding: ${getSpacingToken('100')}px ${getSpacingToken('300')}px;
    flex-grow: 1;
    overflow-y: auto;
    color: ${getToken('textStaticPrimary')};
`;

export const SidePanelAction = styled.div<{ hasScrollBar?: boolean }>`
    padding: calc(${getSpacingToken('300')}px - ${getToken('borderWidth100')}px) ${getSpacingToken('300')}px
        ${getSpacingToken('300')}px;
    border-top: ${getToken('borderWidth100')}px solid transparent;
    transition: ${ANIMATION_DURATION.fast}ms border ease-out;
    ${({ hasScrollBar }) =>
        hasScrollBar &&
        css`
            border-color: ${getToken('borderStaticSecondary')};
            background-color: ${getToken('backgroundStaticRaised')};
        `};
`;

export const SidePanelWrapper = styled((props: Partial<StyledTransitionGroupProps<TransitionGroupChildBaseProps>>) => (
    <StyledTransitionGroup {...props} />
))`
    position: fixed;
    height: 100%;
    width: 100%;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: ${ZIndex.High};
    pointer-events: none;
    overflow: hidden;
`;
